import { createEntityAdapter, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import ActionState, { ActionStatus } from 'store/ActionState';
import { IDed } from 'gql/Request';
import { MsEntraID, GetMsEntraIDOptions, ListMsEntraIDOptions } from 'gql/MsEntraID';

export type MsEntraIDState = {
  msEntraID?: MsEntraID;
  msEntraIDs: EntityState<MsEntraID>;
  loadingState: ActionState;
  saveForm?: MsEntraID;
  mutationState: ActionState;
  mutatedMsEntraIDId?: string;
};

export const msEntraIDAdapter = createEntityAdapter<MsEntraID>();

export const initialState: MsEntraIDState = {
  msEntraIDs: msEntraIDAdapter.getInitialState(),
  loadingState: { status: ActionStatus.IDLE },
  mutationState: { status: ActionStatus.IDLE },
};

const MsEntraIDSlice = createSlice({
  name: 'msEntraID',
  initialState,
  reducers: {
    // GET
    getMsEntraIDbyID: (state, _: PayloadAction<GetMsEntraIDOptions>): MsEntraIDState => {
      return { ...state, loadingState: { status: ActionStatus.PROCESSING } };
    },
    getMsEntraIDbyIDSucceeded: (state, action: PayloadAction<MsEntraID | undefined>): MsEntraIDState => {
      return {
        ...state,
        msEntraID: action.payload,
        loadingState: { status: ActionStatus.SUCCEEDED },
      };
    },
    getMsEntraIDbyIDFailed: (state, action: PayloadAction<Error>): MsEntraIDState => {
      return {
        ...state,
        loadingState: { status: ActionStatus.FAILED, error: action.payload },
      };
    },
    // LIST
    listMsEntraIDs: (state, _: PayloadAction<ListMsEntraIDOptions>): MsEntraIDState => {
      return {
        ...state,
        loadingState: { status: ActionStatus.PROCESSING },
        msEntraIDs: msEntraIDAdapter.getInitialState(),
      };
    },
    listNextPageMsEntraIDs: (state, _: PayloadAction<ListMsEntraIDOptions>): MsEntraIDState => {
      return {
        ...state,
        loadingState: { status: ActionStatus.PROCESSING },
      };
    },
    listMsEntraIDsSucceeded: (state, action: PayloadAction<MsEntraID[]>): MsEntraIDState => {
      return {
        ...state,
        msEntraIDs: msEntraIDAdapter.setMany({ ...state.msEntraIDs }, action.payload),
        loadingState: { status: ActionStatus.SUCCEEDED },
      };
    },
    listMsEntraIDsFailed: (state, action: PayloadAction<Error>): MsEntraIDState => {
      return {
        ...state,
        loadingState: { status: ActionStatus.FAILED, error: action.payload },
      };
    },
    // CREATE
    createMsEntraID: (state, action: PayloadAction<MsEntraID>): MsEntraIDState => {
      return {
        ...state,
        saveForm: action.payload,
        mutationState: { status: ActionStatus.PROCESSING },
        mutatedMsEntraIDId: undefined,
      };
    },
    createMsEntraIDSucceeded: (state, action: PayloadAction<IDed>): MsEntraIDState => {
      return {
        ...state,
        saveForm: undefined,
        mutationState: { status: ActionStatus.SUCCEEDED },
        mutatedMsEntraIDId: action.payload.id,
      };
    },
    createMsEntraIDFailed: (state, action: PayloadAction<Error>): MsEntraIDState => {
      return {
        ...state,
        saveForm: undefined,
        mutationState: { status: ActionStatus.FAILED, error: action.payload },
      };
    },

    // UPDATE
    updateMsEntraID: (state, action: PayloadAction<MsEntraID>): MsEntraIDState => {
      return {
        ...state,
        saveForm: action.payload,
        mutationState: { status: ActionStatus.PROCESSING },
      };
    },
    updateMsEntraIDSucceeded: (state, action: PayloadAction<IDed>): MsEntraIDState => {
      return {
        ...state,
        saveForm: undefined,
        mutationState: { status: ActionStatus.SUCCEEDED },
        mutatedMsEntraIDId: action.payload.id,
      };
    },
    updateMsEntraIDFailed: (state, action: PayloadAction<Error>): MsEntraIDState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.FAILED, error: action.payload },
        saveForm: undefined,
      };
    },

    // DELETE
    deleteMsEntraID: (state, _: PayloadAction<string>): MsEntraIDState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.PROCESSING },
      };
    },
    deleteMsEntraIDSucceeded: (state, action: PayloadAction<IDed>): MsEntraIDState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.SUCCEEDED },
        mutatedMsEntraIDId: action.payload.id,
      };
    },
    deleteMsEntraIDFailed: (state, action: PayloadAction<Error>): MsEntraIDState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.FAILED, error: action.payload },
        saveForm: undefined,
      };
    },

    // CLEAR
    clearState: () => {
      return initialState;
    },
  },
});

export default MsEntraIDSlice;
